import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "react-jss";
import useStyles from "./OpenEndRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { ValidationResultModel } from "../../../models/ValidationResultModel";
import InputBox from "../../shared/InputBox";
import {
    getAriaLabelledByFromId,
    useAriaLabelledByForQuestionOpenEndChoiceBox,
} from "../../../utils/WCAGHelper";

import { StoreState } from "../../../models/states/StoreState";
import { DefaultFocusStatus } from "../../../models/states/DefaultFocusStatus";

interface Props extends RendererProps {
    value?: string;
    name?: string;
    label?: string;
    isDisabled?: boolean;
    isDisabledRequest?: boolean;
    isHidden?: boolean;
    onOpenEndChange?: React.ChangeEventHandler;
    onOpenEndBlur?: React.FocusEventHandler;
    openEndChoiceId?: string;
    openEndType?: string;
    validationResult?: ValidationResultModel;
    size?: string;
    lines?: number;
    style?: any;
    isMouseDownEventPropagation?: boolean;
    isClickEventPropagation?: boolean;
}

export default function OpenEndRenderer(props: Props) {
    const {
        isDisabledRequest,
        isDisabled,
        isHidden,
        onOpenEndBlur,
        onOpenEndChange,
        name,
        value,
        label,
        size,
        lines,
        gridRendering,
        style,
        isMouseDownEventPropagation = false,
        isClickEventPropagation = false,
        question,
        isRowHeader,
        gridRenderedRowIndex,
        gridRenderedColumnIndex,
    } = props;
    const [focused, setFocused] = React.useState(false);
    const theme = useTheme();
    const classes = useStyles({ theme, size, gridRendering });
    const useAriaLabelledBy = useAriaLabelledByForQuestionOpenEndChoiceBox(question);
    const defaultFocusStatus = useSelector(
        (state: StoreState) => state.defaultFocusStatus,
    ) as DefaultFocusStatus;

    useEffect(() => {
        if (name && defaultFocusStatus && defaultFocusStatus.statuses) {
            const newElementId = `${name}_OE_INPUT`;
            let id;
            for (const variable of question.variables) {
                if (variable.name === name) {
                    id = variable.id;
                    break;
                }
                if (variable.choiceList.some(choice => choice.elementId === name)) {
                    id = variable.id;
                    break;
                }
            }
            if (!id) {
                return;
            }
            for (let i = defaultFocusStatus.statuses.length - 1; i >= 0; i--) {
                if (
                    defaultFocusStatus.statuses[i].variableId === id &&
                    defaultFocusStatus.statuses[i].elementId.endsWith("_OE_INPUT")
                ) {
                    defaultFocusStatus.statuses.splice(i, 1);
                }
            }

            let insertIndex = 0;
            for (let i = defaultFocusStatus.statuses.length - 1; i >= 0; i--) {
                const currentElementVariableId = defaultFocusStatus.statuses[i].variableId;
                if (currentElementVariableId === id) {
                    insertIndex = i + 1;
                    break;
                }
            }

            defaultFocusStatus.statuses.splice(insertIndex, 0, {
                elementId: newElementId,
                elementRef: openEndReference,
                variableId: id,
                focusStatus: false,
            });
        }
    }, [name]);

    const openEndReference = useRef(null);

    if (isHidden && !isDisabledRequest) return null;

    let gridTypeLeftLabelOpenEndId: any;
    const parentQuestionType = question?.parent?.type ?? question?.type;
    const isRowOfGrid = gridRendering && isRowHeader;

    if (isRowOfGrid) {
        gridTypeLeftLabelOpenEndId = question?.parent?.elementId ?? question?.elementId;
    }

    return (
        // Wrapper to keep open ends text color when rendering open end inside choices
        <div className={classes.openEnd}>
            <InputBox focused={focused} freeform size="Full">
                <textarea
                    id={`${name}_OE_INPUT`}
                    name={`${name}_OE`}
                    ref={openEndReference}
                    {...(useAriaLabelledBy
                        ? {
                            "aria-labelledby": getAriaLabelledByFromId(
                                question,
                                gridTypeLeftLabelOpenEndId ?? name,
                                isRowOfGrid ? `${parentQuestionType}_OPENENDROWHEADER` : "",
                                (gridRenderedColumnIndex ?? 0) + 1,
                                (gridRenderedRowIndex ?? 0) + 1,
                            ),
                        }
                        : { "aria-label": label })}
                    value={value}
                    onChange={onOpenEndChange}
                    rows={lines}
                    onMouseDown={(e) => {
                        if (!isMouseDownEventPropagation) {
                            e.stopPropagation();
                        }
                    }}
                    onClick={(e) => {
                        // eslint-disable-next-line no-undef
                        if (!isClickEventPropagation) {
                            e.stopPropagation();
                        }
                    }}
                    onFocus={() => {
                        setFocused(true);
                    }}
                    onBlur={(e) => {
                        setFocused(false);
                        onOpenEndBlur(e);
                    }}
                    disabled={isDisabled}
                    style={style}
                />
            </InputBox>
        </div>
    );
}
